import { useGetExternalIntegrationActivitiesQuery } from '@Queries/index';
import * as Api from '@ViewModels';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '@WebComponents/DataGrid';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { css } from 'aphrodite';
import moment from 'moment';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../../../../../models/UiUtils';
import { ClipboardIcon } from '../../../../../../../web/components/svgs/icons/ClipboardIcon';
import { baseStyleSheet } from '../../../../../../../web/styles/styles';
import { styleSheet } from '../../styles';

export const RecentActivityView = ({
	impersonationContext,
	integrationProvider,
}: {
	impersonationContext: Api.IImpersonationContext;
	integrationProvider: Api.IntegrationProvider;
}) => {
	const activitiesQuery = useGetExternalIntegrationActivitiesQuery({
		impersonationContext,
		integrationProvider,
	});
	const isLoadStats = (stats: Api.ProcessedStatsTypes): stats is Api.LoadProcessedStats => {
		return 'loaded' in stats;
	};

	if (activitiesQuery.isPending) return <LoadingSpinner type='large' />;

	const items: Api.ProcessedStatsTypes[] = activitiesQuery.data ?? [];

	const formatJobId = (jobId: string) => {
		if (!jobId || jobId.length <= 12) return jobId;
		const firstSix = jobId.slice(0, 6);
		const lastSix = jobId.slice(-6);
		return `${firstSix}...${lastSix}`;
	};

	const copyJobId = (jobId: string) => {
		copyToClipboard(jobId);
		toast.success('Job ID copied to clipboard');
	};

	return (
		<div className={css(styleSheet.container)}>
			<DataGrid styles={[styleSheet.tableOverrides]}>
				<DataGridColumns>
					<DataGridColumn sticky>Type</DataGridColumn>
					<DataGridColumn sticky>Job Id</DataGridColumn>
					<DataGridColumn sticky>Start Date</DataGridColumn>
					<DataGridColumn sticky>End Date</DataGridColumn>
					<DataGridColumn sticky>Duration</DataGridColumn>
					<DataGridColumn sticky>Total</DataGridColumn>
					<DataGridColumn sticky>Loaded</DataGridColumn>
					<DataGridColumn sticky>Archived</DataGridColumn>
					<DataGridColumn sticky>Skipped</DataGridColumn>
				</DataGridColumns>

				<DataGridRows>
					{!activitiesQuery.isPending &&
						items.map(item => {
							const start = item.start ? moment(item.start).format('MMM DD, hh:mm a') : '-';
							const end = item.end ? moment(item.end).format('MMM DD, hh:mm a') : '-';
							const duration = item.duration ? `${item.duration.substring(0, item.duration.length - 3)}ms` : '-';
							const loaded = isLoadStats(item) ? item.loaded : '-';
							const archived = isLoadStats(item) ? item.archived : '-';
							const skipped = isLoadStats(item) ? item.skipped : '-';
							return (
								<DataGridRow key={`row-${item.jobId}`}>
									<DataGridRowItem>{item._type.replace('ProcessedStats', '')}</DataGridRowItem>
									<DataGridRowItem>
										<button
											className={css(styleSheet.copyButton)}
											onClick={() => copyJobId(item.jobId)}
											title='Click to copy full Job ID'
										>
											{formatJobId(item.jobId)} <ClipboardIcon />
										</button>
									</DataGridRowItem>
									<DataGridRowItem>{start}</DataGridRowItem>
									<DataGridRowItem>{end}</DataGridRowItem>
									<DataGridRowItem>{duration}</DataGridRowItem>
									<DataGridRowItem>
										<h6 className={css(baseStyleSheet.truncateText, baseStyleSheet.m0)}>{item.total}</h6>
									</DataGridRowItem>
									<DataGridRowItem>{loaded}</DataGridRowItem>
									<DataGridRowItem>{archived}</DataGridRowItem>
									<DataGridRowItem>{skipped}</DataGridRowItem>
								</DataGridRow>
							);
						})}

					{activitiesQuery.isPending ? (
						<DataGridRow>
							<DataGridRowItem colSpan={8}>
								<LoadingSpinner type='large' />
							</DataGridRowItem>
						</DataGridRow>
					) : null}
				</DataGridRows>
			</DataGrid>
		</div>
	);
};
